import { useMemo } from "react";

import type { DropdownTab } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import {
    getCategoriesEntriesNames,
    getSubCategoriesEntriesNames,
    getTechnologiesEntriesNames,
} from "app/pages/sales-intelligence/sub-modules/advanced-search/helpers/technologies";
import type {
    CategoryListItemType,
    SubCategoryListItemType,
    TechnologiesBaseListItem,
    TechnologiesConditionEntry,
    TechnologiesDDItemType,
} from "app/pages/sales-intelligence/sub-modules/advanced-search/models/filters/technology/types";
import type {
    SortedTechnologies } from "app/pages/sales-intelligence/modules/technology/types/technologies";
import {
    TechnologyDropdownTabType,
} from "app/pages/sales-intelligence/modules/technology/types/technologies";

type HookParams = {
    isOpen: boolean;
    selectedEntries: readonly TechnologiesConditionEntry[];
    disabledEntries?: readonly TechnologiesConditionEntry[];
    sortedItems: SortedTechnologies;
};

type HookValue = {
    tabs: readonly DropdownTab<TechnologiesDDItemType>[];
};

const useTechnologiesTabs = ({ isOpen, selectedEntries, disabledEntries = [], sortedItems }: HookParams): HookValue => {
    const buildDropdownTabs = (
        categoriesItems: readonly CategoryListItemType[],
        subCategoriesItems: readonly SubCategoryListItemType[],
        technologiesItems: readonly TechnologiesConditionEntry[],
    ): readonly DropdownTab<TechnologiesDDItemType>[] => {
        const selectedCategoriesNames = getCategoriesEntriesNames(selectedEntries);
        const selectedSubCategoriesNames = getSubCategoriesEntriesNames(selectedEntries);
        const selectedTechnologiesNames = getTechnologiesEntriesNames(selectedEntries);

        const disabledCategoriesNames = getCategoriesEntriesNames(disabledEntries || []);
        const disabledSubCategoriesNames = getSubCategoriesEntriesNames(disabledEntries || []);
        const disabledTechnologiesNames = getTechnologiesEntriesNames(disabledEntries || []);

        const addAdditionalUIFlags = (selectedNames: string[], disabledNames: string[]) => {
            return (item: TechnologiesBaseListItem): TechnologiesDDItemType => {
                const isDisabledName = disabledNames.includes(item.name);
                return {
                    ...item,
                    isSelected: isDisabledName || selectedNames.includes(item.name),
                    isDisabled: isDisabledName,
                };
            };
        };

        return [
            {
                id: TechnologyDropdownTabType.Categories,
                name: translate(`si.lead_gen_filters.technologies.dd_tab.categories.name`, {
                    amountOfItems: categoriesItems.length,
                }),
                items: categoriesItems.map(addAdditionalUIFlags(selectedCategoriesNames, disabledCategoriesNames)),
            },
            {
                id: TechnologyDropdownTabType.SubCategories,
                name: translate(`si.lead_gen_filters.technologies.dd_tab.subCategories.name`, {
                    amountOfItems: subCategoriesItems.length,
                }),
                items: subCategoriesItems.map(
                    addAdditionalUIFlags(selectedSubCategoriesNames, disabledSubCategoriesNames),
                ),
            },
            {
                id: TechnologyDropdownTabType.Technologies,
                name: translate(`si.lead_gen_filters.technologies.dd_tab.technologies.name`, {
                    amountOfItems: technologiesItems.length,
                }),
                items: technologiesItems.map(
                    addAdditionalUIFlags(selectedTechnologiesNames, disabledTechnologiesNames),
                ),
            },
        ];
    };

    const tabs = useMemo(() => {
        const [categoriesSortedItems, subCategoriesSortedItems, technologiesSortedItems] = sortedItems;

        return buildDropdownTabs(categoriesSortedItems, subCategoriesSortedItems, technologiesSortedItems);
    }, [isOpen, selectedEntries, sortedItems]);

    return { tabs };
};

export default useTechnologiesTabs;
